import debounce from 'lodash.debounce';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import { BrowseResultItem } from '@cartwhl/common/types';
import PromptInput from '@cartwhl/ui/input/PromptInput';

import { BrowseSearchPreviewNew } from 'components/BrowseMotions';
import { browseMotionStore, navigationStore, projectStore } from 'stores';
import { handleKeyDown } from 'utils/hotkeys';

export default function LibrarySearch() {
  const { suggestions } = useSnapshot(browseMotionStore.state);

  const [text, setText] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const [highlightedSuggestionID, setHighlightedSuggestionID] =
    useState<string>('');
  const [isSuggestionsActive, setIsSuggestionsActive] = useState(false);

  useEffect(() => {
    setHighlightedSuggestionID('');
    setIsSuggestionsActive(false);
  }, [suggestions.data]);

  const debouncedSearch = useRef(
    debounce((searchTerm: string) => {
      cleanupSearch();
      abortControllerRef.current = new AbortController();
      browseMotionStore.initSearchSuggestions(
        { searchTerm },
        abortControllerRef.current.signal,
      );
    }, 300),
  ).current;

  const cleanupSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    debouncedSearch(text);
    // intentionally empty dependency array to run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupSearch();
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="flex flex-col justify-end">
      <div className="dark:bg-gray-90 border-gray-30 dark:border-gray-75 max-w-[672px] flex-1 rounded-t-2xl border-b bg-white">
        <BrowseSearchPreviewNew
          highlightedSuggestionID={highlightedSuggestionID}
          suggestions={suggestions}
        />
      </div>
      <PromptInput
        active={!isSuggestionsActive}
        buttonVariant="neutral"
        buttonText="Search library"
        placeholder="Search for a motion"
        prompt={text}
        hideCursor={isSuggestionsActive}
        onChange={(e) => {
          setText(e.target.value);
          debouncedSearch(e.target.value);
        }}
        onKeyDown={(e) =>
          handleKeyDown(
            e,
            true,
            suggestions as {
              searchTerm: string;
              data: BrowseResultItem[];
              loading: boolean;
              total: number;
            },
            highlightedSuggestionID,
            setHighlightedSuggestionID,
            isSuggestionsActive,
            setIsSuggestionsActive,
          )
        }
        onSubmit={async (e) => {
          e.preventDefault();
          if (highlightedSuggestionID) {
            await projectStore.addExistingMotionToProject({
              createAsNewProject: false,
              existingJobID: highlightedSuggestionID,
              existingVariationNumber: 0,
            });
          } else {
            navigationStore.setModal(StudioModalName.BROWSE, {
              prefillSearchTerm: text,
            });
          }
        }}
        disabled={false}
        loading={false}
        className="flex-none rounded-t-none rounded-b-lg"
      />
    </div>
  );
}
